import { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, BookOpen, CheckCircle2, ChevronDown, ChevronUp, Clock, Info, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { EducationDrawer } from '../education/EducationDrawer';
import { loadEducationTopic } from '../../utils/educationTopics';
import { useEducationPreferences } from '../../hooks/useEducationPreferences';

/**
 * ClientDecisionCard — a client facing an architecture choice that departs from best practice
 * (RESTORE_PLAN R2.7; the client half of the old DecisionCard, which it replaces). It shows the
 * severity and confidence, the recommendation beside the client's choice, why it matters (with
 * the source and a "Learn more" topic), the consequences and mitigations, and the impact on the
 * build; then the actions: follow the recommendation, ask the consultant to review, or (when
 * the choice does not need review) "I understand the risks, proceed anyway" with a reason. Once
 * decided, it shows the decision instead. The consultant's approve and deny live in the Queue
 * (DecisionApprovalCard).
 */

export interface CardDecisionNode {
  id: string;
  title: string;
  options: Array<{ id: string; label: string }>;
  rationale: string[];
  bestPracticeSource?: string;
  educationTopicId?: string;
}

export interface CardWarning {
  message: string;
  consequences: string[];
  mitigations?: string[];
  timeToBite?: string;
}

export interface CardEvaluation {
  severity: 'info' | 'warning' | 'block';
  confidence?: string;
  confidenceReasons?: string[];
  explanation: string;
  isRecommended: boolean;
  recommendedOption?: string;
  selectedOption: string;
  warnings: CardWarning[];
  impacts: Array<{ description: string; estimatedEffort?: string }>;
}

export interface CardDecisionStatus {
  status: 'accepted' | 'proceeded' | 'pending_review' | 'approved' | 'denied' | string;
  reason?: string;
  evaluatedByName?: string;
  resolvedByName?: string;
}

interface ClientDecisionCardProps {
  decisionNode: CardDecisionNode;
  evaluation: CardEvaluation;
  onAcceptRecommendation?: () => void;
  /** Ask the consultant to review; without it the request goes through onProceedAnyway (the old API). */
  onRequestReview?: (reason: string) => void;
  onProceedAnyway?: (reason?: string) => void;
  loading?: boolean;
  userId?: string;
  accessToken?: string;
  /** Extra facts for the education topic's rules. */
  context?: Record<string, any>;
  /** What has been decided already; shown in place of the actions (which return after a denial). */
  status?: CardDecisionStatus | null;
  /** The firm's view: the facts and the status, no client actions. */
  readOnly?: boolean;
}

const SEVERITY = {
  info: { border: 'border-green-200', bg: 'bg-green-50', badge: 'bg-green-600', Icon: CheckCircle2, icon: 'text-green-600', title: 'Best practice aligned' },
  warning: { border: 'border-yellow-200', bg: 'bg-yellow-50', badge: 'bg-yellow-600', Icon: AlertTriangle, icon: 'text-yellow-600', title: 'Requires careful consideration' },
  block: { border: 'border-red-200', bg: 'bg-red-50', badge: 'bg-red-600', Icon: AlertCircle, icon: 'text-red-600', title: 'Consultant review required' },
} as const;

const CONFIDENCE: Record<string, { tone: string; label: string; reasons: string[] }> = {
  high: { tone: 'bg-blue-100 text-blue-800 border-blue-200', label: 'High confidence', reasons: ['Clear best-practice pattern', 'Standard industry approach'] },
  medium: { tone: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Medium confidence', reasons: ['Depends on operating model details', 'Common but varies by team'] },
  low: { tone: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Low confidence', reasons: ['Multiple valid architectures possible', 'Requires specific consultant judgment'] },
};

export function decisionStatusText(status: CardDecisionStatus, recommendedLabel: string, selectedLabel: string): string {
  switch (status.status) {
    case 'accepted': return `You chose to follow the recommendation (${recommendedLabel}).`;
    case 'proceeded': return `${status.evaluatedByName || 'Your team'} chose to proceed with ${selectedLabel}${status.reason ? `: ${status.reason}` : '.'}`;
    case 'pending_review': return 'Waiting for your consultant to review this choice.';
    case 'approved': return `${status.resolvedByName || 'Your consultant'} approved this choice${status.reason ? `: ${status.reason}` : '.'}`;
    case 'denied': return `${status.resolvedByName || 'Your consultant'} recommends a different approach${status.reason ? `: ${status.reason}` : '.'}`;
    default: return '';
  }
}

export function ClientDecisionCard({
  decisionNode,
  evaluation,
  onAcceptRecommendation,
  onRequestReview,
  onProceedAnyway,
  loading = false,
  userId,
  accessToken,
  context,
  status,
  readOnly = false,
}: ClientDecisionCardProps) {
  const [showRationale, setShowRationale] = useState(false);
  const [showConsequences, setShowConsequences] = useState(evaluation.severity !== 'info');
  const [showConfidence, setShowConfidence] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [showEducation, setShowEducation] = useState(false);

  const topic = decisionNode.educationTopicId ? loadEducationTopic(decisionNode.educationTopicId) : null;
  const { isTopicDismissed, dismissTopic, disableAutoJump, isAutoJumpDisabled } = useEducationPreferences(userId || '', accessToken || '');

  // A blocking or low-confidence choice opens the topic once, unless the client dismissed it.
  useEffect(() => {
    if (!topic || !decisionNode.educationTopicId || readOnly || status) return;
    if (evaluation.severity !== 'block' && evaluation.confidence !== 'low') return;
    if (isTopicDismissed(decisionNode.educationTopicId)) return;
    const timer = setTimeout(() => setShowEducation(true), 1000);
    return () => clearTimeout(timer);
  }, [topic, decisionNode.educationTopicId, evaluation.severity, evaluation.confidence, readOnly, status, isTopicDismissed]);

  const sev = SEVERITY[evaluation.severity] ?? SEVERITY.warning;
  const conf = CONFIDENCE[evaluation.confidence ?? ''] ?? null;
  const labelOf = (id?: string) => decisionNode.options.find((o) => o.id === id)?.label ?? id ?? '';
  const recommendedLabel = labelOf(evaluation.recommendedOption);
  const selectedLabel = labelOf(evaluation.selectedOption);
  const requestReview = (why: string) => (onRequestReview ? onRequestReview(why) : onProceedAnyway?.(why));
  const actionsOpen = !readOnly && (!status || status.status === 'denied');
  const Icon = sev.Icon;

  return (
    <>
      <Card className={`border-2 ${sev.border} ${sev.bg}`} data-testid="decision-card" data-severity={evaluation.severity}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-1 items-start gap-3">
              <Icon className={`mt-1 size-6 shrink-0 ${sev.icon}`} aria-hidden="true" />
              <div className="flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <CardTitle>{sev.title}</CardTitle>
                  {conf && (
                    <button type="button" onClick={() => setShowConfidence((v) => !v)} aria-expanded={showConfidence}>
                      <Badge variant="outline" className={conf.tone}>{conf.label}</Badge>
                    </button>
                  )}
                </div>
                <CardDescription>{decisionNode.title}</CardDescription>
                {showConfidence && conf && (
                  <div className="mt-2 rounded border border-gray-200 bg-white/80 p-2">
                    <p>Why this confidence level?</p>
                    <ul className="list-inside list-disc">
                      {(evaluation.confidenceReasons?.length ? evaluation.confidenceReasons : conf.reasons).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className={sev.badge}>{evaluation.severity.toUpperCase()}</Badge>
              {topic && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-600 hover:bg-blue-50 hover:text-blue-800" onClick={() => setShowEducation(true)}>
                  <BookOpen className="mr-1 size-3" aria-hidden="true" /> Learn more
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <p>{evaluation.explanation}</p>

          {!evaluation.isRecommended && evaluation.recommendedOption && (
            <dl className="rounded-lg border border-gray-200 bg-white p-3" data-testid="decision-recommendation">
              <dt className="text-muted-foreground">Recommendation</dt>
              <dd>{recommendedLabel}</dd>
              <dt className="mt-2 text-muted-foreground">Your answer</dt>
              <dd>{selectedLabel}</dd>
            </dl>
          )}

          <div>
            <button type="button" onClick={() => setShowRationale((v) => !v)} aria-expanded={showRationale} className="flex items-center gap-2 text-blue-700 hover:text-blue-800">
              <Info className="size-4" aria-hidden="true" />
              {showRationale ? 'Hide' : 'Show'} why this matters
              {showRationale ? <ChevronUp className="size-4" aria-hidden="true" /> : <ChevronDown className="size-4" aria-hidden="true" />}
            </button>
            {showRationale && (
              <div className="mt-2 space-y-2 pl-6" data-testid="decision-rationale">
                {decisionNode.rationale.map((p, i) => <p key={i}>{p}</p>)}
                {decisionNode.bestPracticeSource && <p className="italic text-muted-foreground">Source: {decisionNode.bestPracticeSource}</p>}
              </div>
            )}
          </div>

          {evaluation.warnings.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowConsequences((v) => !v)} aria-expanded={showConsequences} className="flex items-center gap-2 text-orange-700 hover:text-orange-800">
                <AlertCircle className="size-4" aria-hidden="true" />
                {showConsequences ? 'Hide' : 'Show'} potential consequences ({evaluation.warnings.length})
                {showConsequences ? <ChevronUp className="size-4" aria-hidden="true" /> : <ChevronDown className="size-4" aria-hidden="true" />}
              </button>
              {showConsequences && (
                <div className="mt-2 space-y-3 pl-6" data-testid="decision-consequences">
                  {evaluation.warnings.map((w, i) => (
                    <div key={i} className="rounded-lg border border-orange-200 bg-white p-3">
                      <p className="text-orange-900">{w.message}</p>
                      {w.timeToBite && <Badge variant="outline" className="mt-1 border-orange-300 text-orange-700">Impact: {w.timeToBite.replace('-', ' ')}</Badge>}
                      {w.consequences.length > 0 && (
                        <ul className="mt-2 space-y-0.5 text-muted-foreground">{w.consequences.map((c, j) => <li key={j}>• {c}</li>)}</ul>
                      )}
                      {(w.mitigations?.length ?? 0) > 0 && (
                        <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-2 text-blue-800">
                          <p>What helps</p>
                          <ul className="space-y-0.5">{w.mitigations!.map((m, j) => <li key={j}>• {m}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {evaluation.impacts.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="mb-1">Impact on the build</p>
              <ul className="space-y-1 text-muted-foreground">
                {evaluation.impacts.map((impact, i) => <li key={i}>• {impact.description}{impact.estimatedEffort ? ` (${impact.estimatedEffort})` : ''}</li>)}
              </ul>
            </div>
          )}

          {status && (
            <div role="status" data-testid="decision-status" data-status={status.status} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3">
              {status.status === 'pending_review' ? <Clock className="mt-0.5 size-4 text-blue-600" aria-hidden="true" /> : <Shield className="mt-0.5 size-4 text-gray-600" aria-hidden="true" />}
              <p>{decisionStatusText(status, recommendedLabel, selectedLabel)}{readOnly && status.status === 'pending_review' ? ' Approve or deny it in the Queue\'s Decisions section.' : ''}</p>
            </div>
          )}

          {actionsOpen && (
            <div className="space-y-3 border-t border-gray-200 pt-4" data-testid="decision-actions">
              {onAcceptRecommendation && !evaluation.isRecommended && evaluation.recommendedOption && (
                <Button onClick={onAcceptRecommendation} disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="mr-2 size-4" aria-hidden="true" />
                  Continue with the recommended approach
                </Button>
              )}
              {evaluation.severity === 'block' ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                  <Shield className="mx-auto mb-2 size-8 text-red-600" aria-hidden="true" />
                  <p className="text-red-900">Your consultant reviews this choice before it goes ahead.</p>
                  <Button onClick={() => requestReview(evaluation.confidence === 'low' ? 'Low confidence block: requesting review' : 'Requesting review of this choice')} disabled={loading} className="mt-3 bg-red-600 hover:bg-red-700">
                    Request consultant review
                  </Button>
                </div>
              ) : (
                <>
                  {evaluation.confidence === 'low' && !showOverride && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <p className="text-blue-900">Recommended: a 15-minute consultant review</p>
                      <p className="text-blue-700">This decision has several valid approaches. A quick review prevents rework later.</p>
                      <Button variant="secondary" size="sm" onClick={() => requestReview('Escalation requested due to low confidence')} disabled={loading} className="mt-2 w-full">
                        Request consultant review
                      </Button>
                    </div>
                  )}
                  {evaluation.confidence !== 'low' && (
                    <Button variant="outline" onClick={() => requestReview('Requesting review of this choice')} disabled={loading} className="w-full">
                      Request consultant review
                    </Button>
                  )}
                  {onProceedAnyway && (
                    <Button variant="outline" onClick={() => setShowOverride((v) => !v)} className="w-full border-yellow-300">
                      I understand the risks, proceed anyway
                    </Button>
                  )}
                  {showOverride && onProceedAnyway && (
                    <div className="space-y-2">
                      <label htmlFor="decision-override-reason">Why is this approach right for your organization? (optional)</label>
                      <Textarea id="decision-override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Each region runs its own service desk under separate contracts…" />
                      <Button onClick={() => onProceedAnyway(reason.trim() || undefined)} disabled={loading} className="w-full">
                        Proceed with this approach
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {topic && decisionNode.educationTopicId && (
        <EducationDrawer
          open={showEducation}
          onClose={() => setShowEducation(false)}
          topicId={decisionNode.educationTopicId}
          topic={topic}
          onDismissTopic={dismissTopic}
          onDisableAutoJump={disableAutoJump}
          isAutoJumpDisabled={isAutoJumpDisabled(decisionNode.educationTopicId)}
          context={{ decision: { severity: evaluation.severity, confidence: evaluation.confidence, optionId: evaluation.selectedOption }, ...context }}
        />
      )}
    </>
  );
}
